import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import {
  Camera,
  Receipt as ReceiptIcon,
  Calendar,
  DollarSign,
  ShoppingBag,
  RefreshCw,
  Edit2,
  Trash2,
  Link,
  CheckCircle,
  Eye,
  EyeOff,
  Square,
  CheckSquare,
  Zap,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  Star,
  AlertTriangle,
  Tag,
  Filter,
  Search
} from 'lucide-react';
import apiService from '../services/api';
import ReceiptScanModal from './modals/ReceiptScanModal';
import ReceiptModal from './modals/ReceiptModal';
import LinkModal from './modals/LinkModal';
import TransactionDetailsModal from './modals/TransactionDetailsModal';
import ReceiptVersionHistoryModal from './modals/ReceiptVersionHistoryModal';
import PageHeader from './common/PageHeader';
import { useConfirmation } from '../hooks/useConfirmation';
import { useToast } from '../hooks/useToast';

const ReceiptsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const receiptRefs = useRef({});
  // Holds the receipt id to scroll back to after the next list reload
  // (e.g. after save). Consumed by the scroll effect on the next render.
  const pendingScrollIdRef = useRef(null);
  const [receipts, setReceipts] = useState([]);
  const [allReceipts, setAllReceipts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [hideLinked, setHideLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Server-side pagination cursor — client-side filter/sort still runs on the
  // already-loaded subset. "Load more" fetches the next page and appends.
  const [receiptsTotal, setReceiptsTotal] = useState(0);
  const [hasMoreReceipts, setHasMoreReceipts] = useState(false);
  const PAGE_SIZE = 50;
  const { confirm, ConfirmationModal } = useConfirmation();
  const { showToast, ToastContainer } = useToast();
  const [isReceiptScanModalOpen, setIsReceiptScanModalOpen] = useState(false);
  const [receiptModal, setReceiptModal] = useState({ isOpen: false, receipt: null, editMode: false });
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [matchingReceipt, setMatchingReceipt] = useState(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [versionHistoryModal, setVersionHistoryModal] = useState({ isOpen: false, receiptId: null });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Bulk selection state
  const [selectedReceiptIds, setSelectedReceiptIds] = useState(new Set());
  const [bulkMatchResults, setBulkMatchResults] = useState(null);
  const [approvedMatches, setApprovedMatches] = useState(new Set()); // Track which matches user approved
  const [isBulkMatching, setIsBulkMatching] = useState(false);
  const [bulkMatchMinScore, setBulkMatchMinScore] = useState(15); // Min score threshold for bulk matching
  const [showBulkMatchSettings, setShowBulkMatchSettings] = useState(false); // Show settings before matching

  // Sort state
  const [sortBy, setSortBy] = useState('created_at'); // created_at, receipt_date, merchant, total_amount
  const [sortOrder, setSortOrder] = useState('desc'); // asc or desc

  // Importance filtering state
  const [filterImportance, setFilterImportance] = useState('all'); // all, important, normal
  const [filterTags, setFilterTags] = useState([]); // Array of selected tags
  const [searchQuery, setSearchQuery] = useState(''); // Search by merchant name
  const [searchExpanded, setSearchExpanded] = useState(false); // Whether search input is visible
  const searchInputRef = useRef(null);

  useEffect(() => {
    loadAccounts();
    loadReceipts();
    loadCategories();
  }, []);

  // Handle receipt query parameter for navigation from transactions
  useEffect(() => {
    const receiptId = searchParams.get('receipt');
    if (receiptId && receipts.length > 0) {
      const id = parseInt(receiptId);

      // Expand the receipt
      setExpandedReceipt(id);

      // Scroll to it after a short delay to ensure DOM is ready
      setTimeout(() => {
        const element = receiptRefs.current[id];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Clear the query parameter
      setSearchParams({});
    }
  }, [searchParams, receipts]);

  // Debounce search so we're not hammering the server on every keystroke
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Mirror server results into `receipts` — all filtering and sorting is
  // server-side now so totals and "remaining" counts stay accurate.
  useEffect(() => {
    setReceipts(allReceipts);
  }, [allReceipts]);

  // After a save/reload, scroll back to the receipt the user was viewing.
  // `pendingScrollIdRef` is set synchronously by handleReceiptUpdated just
  // before loadReceipts() fires; we consume it here on the next render
  // cycle that includes the updated receipts list.
  // 100ms setTimeout mirrors the same pattern used for `?receipt=` deep
  // linking — gives the DOM a tick to settle (row refs attached, any
  // images starting to load, etc.) before scrollIntoView fires.
  useEffect(() => {
    const id = pendingScrollIdRef.current;
    if (id == null) return;
    pendingScrollIdRef.current = null; // consume before scheduling so we don't retry
    const timer = setTimeout(() => {
      const el = receiptRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [receipts]);

  const loadAccounts = async () => {
    try {
      const accountsData = await apiService.getAccounts();
      setAccounts(accountsData);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError('Failed to load accounts');
    }
  };

  // Build the current filter payload for the receipts endpoint.
  // Kept as a function (not useMemo) so we can read the latest state from
  // within loadMoreReceipts without re-threading a dependency.
  const currentFilters = () => ({
    accountId: selectedAccountId ?? undefined,
    unlinked: hideLinked ? true : undefined,
    importance: filterImportance,
    tags: filterTags,
    search: debouncedSearchQuery.trim() || undefined,
    sort: sortBy,
    sortOrder,
  });

  // `silent=true` keeps the existing rows rendered while the reload is in
  // flight (no full-page spinner). This matters for the "stay on edited
  // receipt after save" flow: otherwise the rows unmount during load and
  // their refs churn, and scroll-back can miss the target.
  const loadReceipts = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const resp = await apiService.getAllReceipts({
        ...currentFilters(),
        limit: PAGE_SIZE,
        offset: 0,
      });
      setAllReceipts(resp.receipts || []);
      setReceiptsTotal(resp.total ?? (resp.receipts || []).length);
      setHasMoreReceipts(resp.has_more ?? false);
    } catch (err) {
      console.error('Error loading receipts:', err);
      setError('Failed to load receipts');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Reload from page 0 any time a filter, search, or sort changes.
  // Note: excluded from the mount useEffect so we don't double-fire on first render.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    loadReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, hideLinked, filterImportance, filterTags, debouncedSearchQuery, sortBy, sortOrder]);

  const loadMoreReceipts = async () => {
    if (loadingMore || !hasMoreReceipts) return;
    try {
      setLoadingMore(true);
      const resp = await apiService.getAllReceipts({
        ...currentFilters(),
        limit: PAGE_SIZE,
        offset: allReceipts.length,
      });
      setAllReceipts(prev => [...prev, ...(resp.receipts || [])]);
      setReceiptsTotal(resp.total ?? receiptsTotal);
      setHasMoreReceipts(resp.has_more ?? false);
    } catch (err) {
      console.error('Error loading more receipts:', err);
      showToast('Failed to load more receipts', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categories = await apiService.getCategories();
      const options = categories.map(cat => ({
        value: cat.name,
        label: cat.label,
        icon: cat.icon
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleViewReceipt = async (receipt) => {
    try {
      // Fetch full receipt with items
      const fullReceipt = await apiService.getReceipt(receipt.id);
      setReceiptModal({
        isOpen: true,
        receipt: fullReceipt,
        editMode: false
      });
    } catch (err) {
      console.error('Error loading receipt:', err);

      // If receipt no longer exists (404), remove it from the list
      if (err.message?.includes('not found') || err.message?.includes('404')) {
        showToast('This receipt has been deleted. Refreshing the list...', 'info');
        loadReceipts(); // Refresh the receipts list
        return;
      }

      // For other errors, fallback to showing what we have
      setReceiptModal({
        isOpen: true,
        receipt: receipt,
        editMode: false
      });
    }
  };

  const handleViewVersionHistory = (receipt, e) => {
    e.stopPropagation();
    setVersionHistoryModal({
      isOpen: true,
      receiptId: receipt.id
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatCurrencyWithCurrency = (amount, currency = 'USD') => {
    // Map currencies to their appropriate locales for better formatting
    const localeMap = {
      'USD': 'en-US',
      'CAD': 'en-CA',
      'EUR': 'en-GB',
      'GBP': 'en-GB',
    };

    const locale = localeMap[currency] || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  // formatDate imported from utils/dateUtils

  const handleReceiptScanned = async () => {
    // Longer delay to ensure the backend has fully committed the receipt to the database
    await new Promise(resolve => setTimeout(resolve, 1500));
    await loadReceipts(); // Reload receipts after scanning
    setIsReceiptScanModalOpen(false);
  };

  const handleEditReceipt = async (receipt, e) => {
    e.stopPropagation(); // Prevent toggling expansion
    // Fetch full receipt details and open in edit mode
    try {
      const fullReceipt = await apiService.getReceipt(receipt.id);
      setReceiptModal({
        isOpen: true,
        receipt: fullReceipt,
        editMode: true
      });
    } catch (error) {
      console.error('Error loading receipt for editing:', error);
      showToast('Failed to load receipt details', 'error');
    }
  };

  const handleReceiptUpdated = () => {
    // Remember which receipt we were editing so the scroll effect can
    // bring it back into view after the list reloads — otherwise the page
    // jumps to the top and the user has to hunt for what they just saved.
    // Silent reload keeps old rows mounted so their refs survive the
    // fetch (otherwise the spinner unmounts everything mid-reload).
    pendingScrollIdRef.current = receiptModal.receipt?.id ?? null;
    setReceiptModal({ isOpen: false, receipt: null, editMode: false });
    loadReceipts({ silent: true });
  };

  const handleDeleteReceipt = async (receipt, e) => {
    e.stopPropagation(); // Prevent toggling expansion

    const confirmMessage = `Merchant: ${receipt.merchant || 'Unknown'}\nDate: ${formatDate(receipt.receipt_date)}\nAmount: ${formatCurrency(receipt.total_amount)}\n\nThis will also delete the associated transaction.`;

    const confirmed = await confirm('Delete Receipt', confirmMessage, 'danger', 'Delete', 'Cancel');
    if (!confirmed) return;

    try {
      await apiService.deleteReceipt(receipt.id);
      showToast('Receipt deleted successfully', 'success');
      loadReceipts();
    } catch (err) {
      console.error('Error deleting receipt:', err);
      showToast('Failed to delete receipt. Please try again.', 'error');
    }
  };

  const handleToggleImportance = async (receipt, e) => {
    e.stopPropagation(); // Prevent row click

    try {
      // Toggle the is_important flag
      const updatedData = {
        receipt_id: receipt.id,
        merchant: receipt.merchant,
        receipt_date: receipt.receipt_date?.split('T')[0],
        total_amount: receipt.total_amount,
        is_important: !receipt.is_important,
        importance_level: receipt.importance_level || 'normal',
        items: receipt.items || []
      };

      await apiService.updateReceipt(updatedData);

      // Update local state immediately for responsive UI
      setAllReceipts(prev => prev.map(r =>
        r.id === receipt.id ? { ...r, is_important: !receipt.is_important } : r
      ));

      showToast(
        receipt.is_important ? 'Removed from important receipts' : 'Marked as important',
        'success'
      );
    } catch (err) {
      console.error('Error toggling importance:', err);
      showToast('Failed to update receipt importance', 'error');
    }
  };

  const handleMatchTransaction = (receipt, e) => {
    e.stopPropagation(); // Prevent toggling expansion
    setMatchingReceipt(receipt);
    setIsMatchModalOpen(true);
  };

  const handleTransactionMatched = () => {
    setIsMatchModalOpen(false);
    setMatchingReceipt(null);
    loadReceipts(); // Reload receipts after matching
  };

  const handleViewTransaction = async (transactionId, e) => {
    e.stopPropagation();
    try {
      const transaction = await apiService.getTransaction(transactionId);
      const account = accounts.find(a => a.id === transaction.account_id);
      setSelectedTransaction(transaction);
      setSelectedAccount(account);
      setIsTransactionModalOpen(true);
    } catch (err) {
      console.error('Error loading transaction:', err);
      showToast('Failed to load transaction details', 'error');
    }
  };

  const handleTransactionSave = async (transactionId, updates) => {
    try {
      await apiService.updateTransaction(transactionId, updates);
      setIsTransactionModalOpen(false);
      setSelectedTransaction(null);
      setSelectedAccount(null);
      loadReceipts(); // Reload receipts in case transaction was updated
    } catch (err) {
      console.error('Error updating transaction:', err);
      throw err;
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    try {
      await apiService.deleteTransaction(transactionId);
      setIsTransactionModalOpen(false);
      setSelectedTransaction(null);
      setSelectedAccount(null);
      loadReceipts(); // Reload receipts after deletion
    } catch (err) {
      console.error('Error deleting transaction:', err);
      throw err;
    }
  };

  // Bulk selection handlers
  const handleToggleSelectReceipt = (receiptId, e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedReceiptIds);
    if (newSelected.has(receiptId)) {
      newSelected.delete(receiptId);
    } else {
      newSelected.add(receiptId);
    }
    setSelectedReceiptIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedReceiptIds.size === receipts.length) {
      setSelectedReceiptIds(new Set());
    } else {
      setSelectedReceiptIds(new Set(receipts.map(r => r.id)));
    }
  };

  const handleSelectUnlinked = () => {
    const unlinked = receipts.filter(r => !r.transaction_id);
    setSelectedReceiptIds(new Set(unlinked.map(r => r.id)));
  };

  const handleClearSelection = () => {
    setSelectedReceiptIds(new Set());
  };

  const handleBulkMatch = async () => {
    if (selectedReceiptIds.size === 0) {
      showToast('Please select receipts to match', 'warning');
      return;
    }

    // Show settings modal first
    setShowBulkMatchSettings(true);
  };

  const handleStartBulkMatch = async () => {
    setShowBulkMatchSettings(false);
    setIsBulkMatching(true);
    try {
      // Get preview of matches (don't link yet)
      const results = await apiService.bulkMatchReceipts(Array.from(selectedReceiptIds), bulkMatchMinScore, true);
      setBulkMatchResults(results);

      // Pre-approve all matches by default (user can uncheck)
      const matchReceiptIds = results.matched.map(m => m.receipt_id);
      setApprovedMatches(new Set(matchReceiptIds));

      // Don't clear selection or reload yet - wait for user approval
    } catch (err) {
      console.error('Error bulk matching:', err);
      showToast('Failed to find matches. Please try again.', 'error');
      setBulkMatchResults(null);
    } finally {
      setIsBulkMatching(false);
    }
  };

  const handleApplyMatches = async () => {
    if (!bulkMatchResults || approvedMatches.size === 0) {
      showToast('No matches selected to apply', 'warning');
      return;
    }

    setIsBulkMatching(true);
    try {
      // Build list of approved matches to apply
      const matchesToApply = bulkMatchResults.matched
        .filter(m => approvedMatches.has(m.receipt_id))
        .map(m => ({
          receipt_id: m.receipt_id,
          transaction_id: m.transaction_id,
          tip_amount: m.calculated_tip
        }));

      const result = await apiService.applyBulkMatches(matchesToApply);

      showToast(`Successfully linked ${result.summary.applied} receipt${result.summary.applied !== 1 ? 's' : ''}`, 'success');

      // Close modal and reload
      setBulkMatchResults(null);
      setApprovedMatches(new Set());
      setSelectedReceiptIds(new Set());
      loadReceipts();
    } catch (err) {
      console.error('Error applying matches:', err);
      showToast('Failed to apply matches. Please try again.', 'error');
    } finally {
      setIsBulkMatching(false);
    }
  };

  const toggleMatchApproval = (receiptId) => {
    setApprovedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) {
        newSet.delete(receiptId);
      } else {
        newSet.add(receiptId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <PageHeader
        icon={ReceiptIcon}
        title="Receipts"
        subtitle="Track and manage your receipt history"
      >
        <button
          onClick={loadReceipts}
          disabled={loading}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} sm:mr-2`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        <button
          onClick={() => setIsReceiptScanModalOpen(true)}
          disabled={accounts.length === 0}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          <Camera className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Scan Receipt</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Unified Toolbar */}
        <div className="mb-6 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            {/* Left side: Filters and Sort */}
            <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
              {/* Account Filter */}
              <div className="flex-1 min-w-[200px]">
                <select
                  value={selectedAccountId || ''}
                  onChange={(e) => {
                    const accountId = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedAccountId(accountId);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-green-500 focus:border-green-500 rounded-md"
                >
                  <option value="">All Accounts</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.institution})
                    </option>
                  ))}
                </select>
              </div>

              {/* Hide Linked Toggle */}
              <button
                onClick={() => setHideLinked(!hideLinked)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                {hideLinked ? (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Linked
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Linked
                  </>
                )}
              </button>

              {/* Importance Filter */}
              <div className="flex-shrink-0 min-w-[160px]">
                <select
                  value={filterImportance}
                  onChange={(e) => setFilterImportance(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-green-500 focus:border-green-500 rounded-md"
                >
                  <option value="all">All Receipts</option>
                  <option value="important">Important Only</option>
                  <option value="normal">Normal Only</option>
                </select>
              </div>
            </div>

            {/* Right side: Bulk Actions (only shown when receipts are selected) */}
            {selectedReceiptIds.size > 0 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 pt-4 lg:pt-0 lg:pl-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedReceiptIds.size} selected
                  </span>
                  <button
                    onClick={handleClearSelection}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleSelectAll}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <CheckSquare className="h-3.5 w-3.5 mr-1.5" />
                    All ({receipts.length})
                  </button>
                  <button
                    onClick={handleSelectUnlinked}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <Link className="h-3.5 w-3.5 mr-1.5" />
                    Unlinked ({receipts.filter(r => !r.transaction_id).length})
                  </button>
                  <button
                    onClick={handleBulkMatch}
                    disabled={isBulkMatching}
                    className="inline-flex items-center px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <Zap className={`h-4 w-4 mr-1.5 ${isBulkMatching ? 'animate-pulse' : ''}`} />
                    {isBulkMatching ? 'Matching...' : 'Auto-Match'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Receipts List */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                Receipt History
                {receipts.length > 0 && (
                  <span className="ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">({receipts.length})</span>
                )}
              </h2>
              {receipts.length > 0 && (
                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 dark:text-green-500" />
                    <span className="text-gray-600 dark:text-gray-400">
                      {receipts.filter(r => r.transaction_id).length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600 dark:text-blue-500" />
                    <span className="text-gray-600 dark:text-gray-400">
                      {receipts.filter(r => !r.transaction_id).length}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin mx-auto text-gray-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading receipts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mx-auto max-w-md">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            </div>
          ) : receipts.length === 0 && allReceipts.length === 0 ? (
            <div className="text-center py-12">
              <ReceiptIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No receipts yet</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Start by scanning a receipt with your camera
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setIsReceiptScanModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Scan Your First Receipt
                </button>
              </div>
            </div>
          ) : (
            <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto max-w-full">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="w-12 px-3 py-3 text-center">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                        title={selectedReceiptIds.size === receipts.length ? "Deselect all" : "Select all"}
                      >
                        {selectedReceiptIds.size === receipts.length ? (
                          <CheckSquare className="h-5 w-5" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th className="w-10 px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <Star className="h-4 w-4 inline-block" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => !searchExpanded && !searchQuery && handleSort('merchant')}
                    >
                      {searchExpanded || searchQuery ? (
                        /* Expanded search input */
                        <div className="relative max-w-[200px]" onClick={(e) => e.stopPropagation()}>
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onBlur={() => !searchQuery && setSearchExpanded(false)}
                            onKeyDown={(e) => e.key === 'Escape' && (setSearchQuery(''), setSearchExpanded(false))}
                            placeholder="Search merchant..."
                            autoFocus
                            className="w-full pl-2 pr-6 py-0.5 text-xs font-normal normal-case border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500"
                          />
                          <button
                            onClick={() => { setSearchQuery(''); setSearchExpanded(false); }}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        /* Collapsed: show label + search icon on left, sort icon on right */
                        <div className="flex items-center justify-between w-full">
                          <span
                            onClick={(e) => { e.stopPropagation(); setSearchExpanded(true); }}
                            className="flex items-center gap-1.5 cursor-pointer hover:text-gray-700 dark:hover:text-gray-100"
                            title="Search receipts"
                          >
                            Receipt
                            <Search className="h-3 w-3 opacity-50" />
                          </span>
                          {sortBy === 'merchant' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-30" />
                          )}
                        </div>
                      )}
                    </th>
                    <th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Account
                    </th>
                    <th
                      className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('receipt_date')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Receipt Date</span>
                        {sortBy === 'receipt_date' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th
                      className="w-36 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Date Imported</span>
                        {sortBy === 'created_at' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th
                      className="w-28 px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('total_amount')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <span>Amount</span>
                        {sortBy === 'total_amount' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-30" />
                        )}
                      </div>
                    </th>
                    <th className="w-28 px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {receipts.map((receipt) => (
                    <React.Fragment key={receipt.id}>
                      <tr
                        ref={el => receiptRefs.current[receipt.id] = el}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                          receipt.is_important
                            ? receipt.importance_level === 'critical'
                              ? 'border-l-4 border-red-500 bg-red-50/30 dark:bg-red-900/10'
                              : 'border-l-4 border-yellow-500 bg-yellow-50/30 dark:bg-yellow-900/10'
                            : ''
                        }`}
                        onClick={() => handleViewReceipt(receipt)}
                      >
                        <td className="px-3 py-4 text-center">
                          <button
                            onClick={(e) => handleToggleSelectReceipt(receipt.id, e)}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            {selectedReceiptIds.has(receipt.id) ? (
                              <CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-500" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-4 text-center">
                          <button
                            onClick={(e) => handleToggleImportance(receipt, e)}
                            className="text-gray-400 dark:text-gray-500 hover:text-yellow-500 transition-colors"
                            title={receipt.is_important ? "Remove from important" : "Mark as important"}
                          >
                            {receipt.is_important ? (
                              <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                            ) : (
                              <Star className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {receipt.merchant || 'Unknown Merchant'}
                              </span>
                                {receipt.transaction_id && (
                                  <button
                                    onClick={(e) => handleViewTransaction(receipt.transaction_id, e)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 text-xs font-medium hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    Linked
                                  </button>
                                )}
                                {receipt.version_count > 0 && (
                                  <button
                                    onClick={(e) => handleViewVersionHistory(receipt, e)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
                                    title={`${receipt.version_count} version${receipt.version_count !== 1 ? 's' : ''}`}
                                  >
                                    <Clock className="h-3 w-3" />
                                    {receipt.version_count}
                                  </button>
                                )}
                                {receipt.importance_level === 'critical' && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 text-xs font-medium"
                                    title="Critical importance"
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    Critical
                                  </span>
                                )}
                                {receipt.expiration_date && (
                                  (() => {
                                    const daysUntilExpiry = Math.ceil((new Date(receipt.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
                                    if (daysUntilExpiry <= 30) {
                                      return (
                                        <span
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                            daysUntilExpiry <= 7
                                              ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
                                              : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-200'
                                          }`}
                                          title={`Expires: ${formatDate(receipt.expiration_date)}`}
                                        >
                                          <AlertTriangle className="h-3 w-3" />
                                          {daysUntilExpiry <= 0 ? 'Expired' : `${daysUntilExpiry}d left`}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()
                                )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {receipt.items_count} item{receipt.items_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {receipt.transaction_id ? (
                              receipt.account_name
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(receipt.receipt_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {receipt.created_at ? formatDate(receipt.created_at) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrencyWithCurrency(receipt.total_amount, accounts.find(a => a.id === receipt.account_id)?.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {!receipt.transaction_id && (
                              <button
                                onClick={(e) => handleMatchTransaction(receipt, e)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                                title="Match to transaction"
                              >
                                <Link className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleEditReceipt(receipt, e)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-md transition-colors"
                              title="Edit receipt"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteReceipt(receipt, e)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                              title="Delete receipt"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Sort & Search Bar */}
            <div className="md:hidden px-3 py-2 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
              {searchExpanded || searchQuery ? (
                /* Expanded search */
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => !searchQuery && setSearchExpanded(false)}
                    placeholder="Search merchant..."
                    autoFocus
                    className="w-full pl-2 pr-7 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <button
                    onClick={() => { setSearchQuery(''); setSearchExpanded(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                /* Collapsed: search icon + sort controls */
                <>
                  <button
                    onClick={() => setSearchExpanded(true)}
                    className="p-1.5 border border-gray-300 dark:border-gray-600 rounded text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    title="Search"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 text-xs border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded px-2 py-1.5"
                  >
                    <option value="created_at">Date Imported</option>
                    <option value="receipt_date">Receipt Date</option>
                    <option value="merchant">Merchant</option>
                    <option value="total_amount">Amount</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-1.5 border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700"
                  >
                    {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                  </button>
                </>
              )}
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden p-3 space-y-2">
              {receipts.map((receipt) => (
                <div
                  key={receipt.id}
                  ref={el => receiptRefs.current[receipt.id] = el}
                  onClick={() => handleViewReceipt(receipt)}
                  className={`bg-white dark:bg-gray-800 p-2.5 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${
                    receipt.is_important
                      ? receipt.importance_level === 'critical'
                        ? 'border-l-4 border-red-500 bg-red-50/30 dark:bg-red-900/10'
                        : 'border-l-4 border-yellow-500 bg-yellow-50/30 dark:bg-yellow-900/10'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {/* Checkbox and Header Row */}
                  <div className="flex items-start gap-2 mb-1.5">
                    <button
                      onClick={(e) => handleToggleSelectReceipt(receipt.id, e)}
                      className="mt-0.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {selectedReceiptIds.has(receipt.id) ? (
                        <CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-500" />
                      ) : (
                        <Square className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={(e) => handleToggleImportance(receipt, e)}
                      className="mt-0.5 text-gray-400 dark:text-gray-500 hover:text-yellow-500 transition-colors"
                      title={receipt.is_important ? "Remove from important" : "Mark as important"}
                    >
                      {receipt.is_important ? (
                        <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                      ) : (
                        <Star className="h-5 w-5" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                  {/* Header Row: Date and Amount */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3 flex-shrink-0" />
                        <span>{formatDate(receipt.receipt_date)}</span>
                      </div>
                      {receipt.created_at && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 pl-4.5">
                          Imported: {formatDate(receipt.created_at)}
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrencyWithCurrency(receipt.total_amount, accounts.find(a => a.id === receipt.account_id)?.currency)}
                    </div>
                  </div>

                  {/* Merchant with icon */}
                  <div className="flex items-start gap-1.5 mb-1.5">
                    <ShoppingBag className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                        {receipt.merchant || 'Unknown Merchant'}
                      </div>
                    </div>
                  </div>

                  {/* Badges and Actions Row */}
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {receipt.items_count} item{receipt.items_count !== 1 ? 's' : ''}
                      </span>
                      {receipt.transaction_id && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <button
                            onClick={(e) => handleViewTransaction(receipt.transaction_id, e)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 text-xs font-medium hover:bg-green-200 dark:hover:bg-green-800"
                          >
                            <CheckCircle className="h-3 w-3" />
                            Linked
                          </button>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{receipt.account_name}</span>
                        </>
                      )}
                      {receipt.version_count > 0 && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <button
                            onClick={(e) => handleViewVersionHistory(receipt, e)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800"
                            title={`${receipt.version_count} version${receipt.version_count !== 1 ? 's' : ''}`}
                          >
                            <Clock className="h-3 w-3" />
                            {receipt.version_count}
                          </button>
                        </>
                      )}
                      {receipt.importance_level === 'critical' && (
                        <>
                          <span className="text-gray-300 dark:text-gray-600">•</span>
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 text-xs font-medium"
                            title="Critical importance"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Critical
                          </span>
                        </>
                      )}
                      {receipt.expiration_date && (
                        (() => {
                          const daysUntilExpiry = Math.ceil((new Date(receipt.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
                          if (daysUntilExpiry <= 30) {
                            return (
                              <>
                                <span className="text-gray-300 dark:text-gray-600">•</span>
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                    daysUntilExpiry <= 7
                                      ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
                                      : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-200'
                                  }`}
                                  title={`Expires: ${formatDate(receipt.expiration_date)}`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  {daysUntilExpiry <= 0 ? 'Expired' : `${daysUntilExpiry}d left`}
                                </span>
                              </>
                            );
                          }
                          return null;
                        })()
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {!receipt.transaction_id && (
                        <button
                          onClick={(e) => handleMatchTransaction(receipt, e)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                          title="Match"
                        >
                          <Link className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleEditReceipt(receipt, e)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteReceipt(receipt, e)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load more + pagination status — total reflects filtered
                count since all filtering is server-side */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {allReceipts.length} of {receiptsTotal} receipts
              </p>
              {hasMoreReceipts && (
                <button
                  onClick={loadMoreReceipts}
                  disabled={loadingMore}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore
                    ? 'Loading...'
                    : `Load more (${receiptsTotal - allReceipts.length} remaining)`}
                </button>
              )}
            </div>
            </>
          )}
        </div>
      </div>

      {/* Receipt Scan Modal */}
      {accounts.length > 0 && (
        <ReceiptScanModal
          isOpen={isReceiptScanModalOpen}
          onClose={() => setIsReceiptScanModalOpen(false)}
          account={accounts.find(a => a.id === selectedAccountId) || accounts[0]}
          onItemsExtracted={handleReceiptScanned}
        />
      )}

      {/* Unified Receipt Modal (View/Edit) */}
      <ReceiptModal
        isOpen={receiptModal.isOpen}
        onClose={() => setReceiptModal({ isOpen: false, receipt: null, editMode: false })}
        receipt={receiptModal.receipt}
        accounts={accounts}
        currentAccount={receiptModal.receipt ? accounts.find(a => a.id === receiptModal.receipt.account_id) : null}
        onReceiptUpdated={handleReceiptUpdated}
        initialEditMode={receiptModal.editMode}
        onViewTransaction={async (transactionId) => {
          // Close the receipt modal first
          setReceiptModal({ isOpen: false, receipt: null, editMode: false });
          // Then open the transaction modal
          try {
            const transaction = await apiService.getTransaction(transactionId);
            const account = accounts.find(a => a.id === transaction.account_id);
            setSelectedTransaction(transaction);
            setSelectedAccount(account);
            setIsTransactionModalOpen(true);
          } catch (err) {
            console.error('Error loading transaction:', err);
            showToast('Failed to load transaction details', 'error');
          }
        }}
      />

      {/* Match Transaction Modal */}
      {matchingReceipt && (
        <LinkModal
          isOpen={isMatchModalOpen}
          onClose={() => {
            setIsMatchModalOpen(false);
            setMatchingReceipt(null);
          }}
          receipt={matchingReceipt}
          onLinked={handleTransactionMatched}
        />
      )}

      {/* Transaction Details Modal */}
      {selectedTransaction && selectedAccount && (
        <TransactionDetailsModal
          isOpen={isTransactionModalOpen}
          onClose={() => {
            setIsTransactionModalOpen(false);
            setSelectedTransaction(null);
            setSelectedAccount(null);
          }}
          transaction={selectedTransaction}
          categoryOptions={categoryOptions}
          onSave={handleTransactionSave}
          onDelete={handleTransactionDelete}
          onCategoryCreated={loadCategories}
          onTransactionUpdated={loadReceipts}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}

      {/* Bulk Match Settings Modal */}
      {showBulkMatchSettings && (
        <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Bulk Match Settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum Match Score: {bulkMatchMinScore}/20
                </label>
                <input
                  type="range"
                  min="5"
                  max="20"
                  value={bulkMatchMinScore}
                  onChange={(e) => setBulkMatchMinScore(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>5 (More matches)</span>
                  <span>20 (Fewer, better matches)</span>
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="mb-2">Higher scores mean stricter matching:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>5-10</strong>: Very loose matching, may include poor matches</li>
                  <li><strong>11-14</strong>: Moderate matching, good balance</li>
                  <li><strong>15-17</strong>: Strict matching (recommended)</li>
                  <li><strong>18-20</strong>: Very strict, only near-perfect matches</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowBulkMatchSettings(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleStartBulkMatch}
                disabled={isBulkMatching}
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isBulkMatching ? 'Finding Matches...' : 'Find Matches'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Match Results Modal */}
      {bulkMatchResults && (
        <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Bulk Match Results
                </h3>
                <button
                  onClick={() => setBulkMatchResults(null)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {bulkMatchResults.summary.total}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-500">
                    {bulkMatchResults.summary.matched}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Matched</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-500">
                    {bulkMatchResults.summary.unmatched}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Unmatched</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-500">
                    {bulkMatchResults.summary.errors}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Errors</div>
                </div>
              </div>
            </div>

            {/* Results Details */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Matched Receipts */}
              {bulkMatchResults.matched.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-3">
                    Found Matches ({approvedMatches.size} of {bulkMatchResults.matched.length} selected)
                  </h4>
                  <div className="space-y-2">
                    {bulkMatchResults.matched.map((match, idx) => (
                      <div key={idx} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={approvedMatches.has(match.receipt_id)}
                            onChange={() => toggleMatchApproval(match.receipt_id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-green-900 dark:text-green-100">
                              {match.merchant || 'Unknown Merchant'}
                            </div>
                            <div className="text-xs text-green-700 dark:text-green-300 mt-1">
                              Matched to: {match.transaction_description}
                            </div>
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              Score: {match.match_score}/20 • {formatCurrency(match.receipt_amount)} → {formatCurrency(match.transaction_amount)}
                              {match.calculated_tip > 0 && ` • Tip: ${formatCurrency(match.calculated_tip)} (${match.tip_percentage}%)`}
                            </div>
                          </div>
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Receipts */}
              {bulkMatchResults.unmatched.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-3">
                    Not Matched ({bulkMatchResults.unmatched.length})
                  </h4>
                  <div className="space-y-2">
                    {bulkMatchResults.unmatched.map((item, idx) => (
                      <div key={idx} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm">
                        <div className="font-medium text-yellow-900 dark:text-yellow-100">
                          {item.merchant || 'Unknown Merchant'}
                        </div>
                        <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                          Reason: {item.reason}
                        </div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          {formatDate(item.receipt_date)} • {formatCurrency(item.receipt_amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {bulkMatchResults.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-900 dark:text-red-100 mb-3">
                    Errors ({bulkMatchResults.errors.length})
                  </h4>
                  <div className="space-y-2">
                    {bulkMatchResults.errors.map((error, idx) => (
                      <div key={idx} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
                        <div className="font-medium text-red-900 dark:text-red-100">
                          Receipt ID: {error.receipt_id}
                        </div>
                        <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                          {error.error}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setBulkMatchResults(null);
                  setApprovedMatches(new Set());
                }}
                className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyMatches}
                disabled={approvedMatches.size === 0 || isBulkMatching}
                className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isBulkMatching ? 'Applying...' : `Apply ${approvedMatches.size} Match${approvedMatches.size !== 1 ? 'es' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      <ReceiptVersionHistoryModal
        isOpen={versionHistoryModal.isOpen}
        onClose={() => setVersionHistoryModal({ isOpen: false, receiptId: null })}
        receiptId={versionHistoryModal.receiptId}
        onVersionRestored={() => {
          setVersionHistoryModal({ isOpen: false, receiptId: null });
          loadReceipts();
        }}
      />

      <ConfirmationModal />
      <ToastContainer />
    </div>
  );
};

export default ReceiptsPage;
